import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

type Blog = {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  category: string;
  featured_image_url: string | null;
  is_published: boolean;
  created_at: string;
};

const categories = ["implants", "cosmetic-dentistry", "kids-dental-care", "general-dental-care", "oral-hygiene"];
const emptyForm = { title: "", slug: "", content: "", excerpt: "", category: "general-dental-care", featured_image_url: "", is_published: false };

const AdminBlog = () => {
  const [posts, setPosts] = useState<Blog[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchPosts = async () => {
    const { data } = await supabase.from("blogs").select("*").order("created_at", { ascending: false });
    if (data) setPosts(data);
  };

  useEffect(() => { fetchPosts(); }, []);

  const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const uploadImage = async (file: File) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `blog/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("admin-uploads").upload(path, file);
    if (error) { toast.error("Upload failed"); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from("admin-uploads").getPublicUrl(path);
    setForm({ ...form, featured_image_url: publicUrl });
    setUploading(false);
  };

  const handleSave = async () => {
    const payload = {
      title: form.title,
      slug: form.slug || generateSlug(form.title),
      content: form.content,
      excerpt: form.excerpt || null,
      category: form.category,
      featured_image_url: form.featured_image_url || null,
      is_published: form.is_published,
    };

    if (editId) {
      await supabase.from("blogs").update(payload).eq("id", editId);
      toast.success("Post updated");
    } else {
      await supabase.from("blogs").insert(payload);
      toast.success("Post created");
    }
    setOpen(false);
    setForm(emptyForm);
    setEditId(null);
    fetchPosts();
  };

  const handleEdit = (post: Blog) => {
    setForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || "",
      category: post.category,
      featured_image_url: post.featured_image_url || "",
      is_published: post.is_published,
    });
    setEditId(post.id);
    setOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    await supabase.from("blogs").delete().eq("id", id);
    toast.success("Post deleted");
    fetchPosts();
  };

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Hero */}
      <section className="admin-gradient-hero rounded-3xl px-6 lg:px-10 py-8 lg:py-10">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-semibold">Posts</h1>
            <p className="text-white/70 mt-2 text-sm lg:text-base">Manage all your live articles and drafts — {posts.length} total.</p>
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition shadow-lg">
                <Plus className="h-4 w-4" /> Create New Post
              </button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Edit Post" : "New Post"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: generateSlug(e.target.value) })} />
              <Input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              <Input placeholder="Excerpt (short description)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea placeholder="Content (supports markdown)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={10} />

              <div>
                <label className="text-xs font-medium mb-1 block">Featured Image</label>
                {form.featured_image_url && <img src={form.featured_image_url} alt="Featured" className="h-32 rounded mb-1 object-cover" />}
                <Input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} disabled={uploading} />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
                <span className="text-sm">Published</span>
              </div>

              <Button onClick={handleSave} className="w-full" disabled={!form.title}>
                {editId ? "Update" : "Create"} Post
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </section>

      {/* Tabs */}
      <div className="admin-card px-4 py-3 flex items-center gap-6 text-sm font-medium overflow-x-auto">
        <span className="text-[hsl(var(--admin-teal-deep))] border-b-2 border-[hsl(var(--admin-teal))] pb-1.5">All ({posts.length})</span>
        <span className="text-[hsl(var(--admin-muted))] pb-1.5">Published ({posts.filter(p => p.is_published).length})</span>
        <span className="text-[hsl(var(--admin-muted))] pb-1.5">Drafts ({posts.filter(p => !p.is_published).length})</span>
      </div>

      <div className="space-y-3">
        {posts.map((post) => (
          <div key={post.id} className="admin-card p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {post.featured_image_url ? (
                <img src={post.featured_image_url} alt="" className="h-14 w-20 object-cover rounded-lg shrink-0" />
              ) : (
                <div className="h-14 w-20 rounded-lg bg-[hsl(var(--admin-bg))] flex items-center justify-center text-[hsl(var(--admin-muted))] shrink-0">
                  <Plus className="h-5 w-5 opacity-30" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{post.title}</p>
                <p className="text-xs text-[hsl(var(--admin-muted))] mt-0.5">
                  <span className="inline-block px-2 py-0.5 rounded bg-[hsl(var(--admin-teal))]/10 text-[hsl(var(--admin-teal-deep))] mr-2 uppercase tracking-wider text-[10px] font-semibold">
                    {post.category}
                  </span>
                  {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-wider font-semibold ${post.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {post.is_published ? "Published" : "Draft"}
              </span>
              <Switch checked={post.is_published} onCheckedChange={async () => {
                await supabase.from("blogs").update({ is_published: !post.is_published }).eq("id", post.id);
                fetchPosts();
              }} />
              <Button size="icon" variant="ghost" aria-label="Edit blog post" onClick={() => handleEdit(post)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" aria-label="Delete blog post" onClick={() => handleDelete(post.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-[hsl(var(--admin-muted))] text-center py-12 admin-card">No blog posts yet.</p>}
      </div>
    </div>
  );
};

export default AdminBlog;
